const getUniqueReportingYears = (entities) => {
  let uniqueReportingYears = new Set();

  entities
    .filter((entity) => entity.type === "year")
    .forEach(({ properties }) => {
      properties.forEach(({ type, mentionText }) => {
        if (type === "reporting-year") uniqueReportingYears.add(mentionText);
      });
    });

  return [...uniqueReportingYears];
};

const sanitizeValue = (value) => {
  if (!value) return 0;
  const cleanedValue = value.replace(/[\s,()]/g, "");
  return value.startsWith("(") || value.startsWith("-")
    ? -parseInt(cleanedValue)
    : parseInt(cleanedValue);
};

const sumPropertyValuesWithNegative = (properties, type) => {
  const filteredProperties = properties.filter((prop) => prop.type === type);
  return filteredProperties.length > 0
    ? filteredProperties
        .reduce((sum, { mentionText }) => {
          const numericValue =
            mentionText.startsWith("(") || mentionText.startsWith("-")
              ? -parseInt(mentionText.replace(/[\s,()]/g, ""))
              : parseInt(mentionText.replace(/[\s,()]/g, ""));
          return sum + (isNaN(numericValue) ? 0 : numericValue);
        }, 0)
        .toString()
    : "0";
};

const sumPropertyValues = (properties, type) => {
  const filteredProperties = properties.filter((prop) => prop.type === type);
  return filteredProperties.length > 0
    ? filteredProperties
        .reduce((sum, { mentionText }) => {
          const numericValue = parseInt(mentionText.replace(/[\s,()]/g, ""));
          return sum + (isNaN(numericValue) ? 0 : numericValue);
        }, 0)
        .toString()
    : "0";
};

// Validation Flags in Label Editor Screen
export const validateLabels = (splitFilesData) => {
  const labelsToValidate = {
    balanceSheet: {
      "reporting-dates": [],
      "total-assets": [],
      "total-liability-net-worth": [],
    },
    profitAndLoss: {
      "reporting-dates": [],
      "gross-profit": [],
      "operating-profit": [],
      "net-income-after-tax": [],
      "gross-contract-income": [],
    },
    cashflow: {
      "reporting-dates": [],
      "net-cash-operating": [],
      "net-cash-flow": [],
    },
    workCompleted: {
      as_of_date: [],
      projectSpecific: {},
    },
    workInProgress: {
      as_of_date: [],
      projectSpecific: {},
    },
  };
  const balanceSheet = (splitFilesData) => {
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "balanceSheet"
    );

    if (!matchedSplit) {
      return;
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return;
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        labelsToValidate["balanceSheet"]["reporting-dates"].push({
          hasFailed: true,
          message: `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`,
        });
      } else {
        labelsToValidate["balanceSheet"]["reporting-dates"].push({
          hasFailed: false,
          message: `Reporting date format validation passed: "${reportingDate}"`,
        });
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map((year) => ({
      reportingYear: year,
      totalAssets: "",
      cash: "",
      marketableSecurities: "",
      accountsRecievableProgress: "",
      accountsRecievableRetention: "",
      defferedAssets: "",
      inventory: "",
      prepaidExpensesCurrent: "",
      otherCurrentAssets: "",
      totalCurrentAssets: "",
      prepaidExpensesNonCurrent: "",
      accountsRecievableOverNinetyDays: "",
      plantsAndEquipment: "",
      machineryAndVehicles: "",
      furnitureAndFixture: "",
      realEstate: "",
      otherFixedAssets: "",
      accumulatedDepreciation: "",
      otherNonCurrentAssets: "",
      assetsOnLease: "",
      cashRestricted: "",
      shareholderLoans: "",
      totalLiabilityAndNetworth: "",
      assetsWrittenOff: "",
      loanPayable: "",
      billsPayable: "",
      accountsPayableProgress: "",
      accountsPayableRetention: "",
      otherPayables: "",
      deferredRevenue: "",
      currentPortionLongTermDebt: "",
      currentLeaseLiability: "",
      employeeLoans: "",
      currentTaxes: "",
      deferredTaxes: "",
      loansFromShareholders: "",
      accruedExpenses: "",
      totalCurrentLiabilities: "",
      longTermLoanPayable: "",
      longTermLeaseLiability: "",
      longTermDebtOther: "",
      longTermDeferredTax: "",
      otherLiabilities: "",
      minorityInterest: "",
      totalLiabilities: "",
      subordinatedDebt: "",
      shareCapital: "",
      intangibleAssets: "",
      retainedEarnings: "",
      netWorth: "",
      otherEquity: "",
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties
      );

      formula[index].cash = sumPropertyValuesWithNegative(
        mergedProperties,
        "cash"
      );
      formula[index].totalAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-assets"
      );
      formula[index].marketableSecurities = sumPropertyValuesWithNegative(
        mergedProperties,
        "marketable-securities"
      );
      formula[index].accountsRecievableProgress = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-receivable-progress"
      );
      formula[index].accountsRecievableRetention =
        sumPropertyValuesWithNegative(
          mergedProperties,
          "accounts-receivable-retention"
        );
      formula[index].defferedAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-assets"
      );
      formula[index].inventory = sumPropertyValuesWithNegative(
        mergedProperties,
        "inventory"
      );
      formula[index].prepaidExpensesCurrent = sumPropertyValuesWithNegative(
        mergedProperties,
        "prepaid-expenses-current"
      );
      formula[index].otherCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-current-assets"
      );
      formula[index].totalCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-current-assets"
      );
      formula[index].prepaidExpensesNonCurrent = sumPropertyValuesWithNegative(
        mergedProperties,
        "prepaid-expenses-non-current"
      );
      formula[index].accountsRecievableOverNinetyDays =
        sumPropertyValuesWithNegative(
          mergedProperties,
          "accounts-receivable-over-90days"
        );
      formula[index].plantsAndEquipment = sumPropertyValuesWithNegative(
        mergedProperties,
        "plant-and-equipment"
      );
      formula[index].machineryAndVehicles = sumPropertyValuesWithNegative(
        mergedProperties,
        "machinery-and-vehicles"
      );
      formula[index].furnitureAndFixture = sumPropertyValuesWithNegative(
        mergedProperties,
        "furniture-and-fixture"
      );
      formula[index].realEstate = sumPropertyValuesWithNegative(
        mergedProperties,
        "real-estate"
      );
      formula[index].otherFixedAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-fixed-assets"
      );
      formula[index].accumulatedDepreciation = sumPropertyValues(
        mergedProperties,
        "accumulated-depreciation"
      );
      formula[index].otherNonCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-non-current-assets"
      );
      formula[index].assetsOnLease = sumPropertyValuesWithNegative(
        mergedProperties,
        "assets-on-lease"
      );
      formula[index].cashRestricted = sumPropertyValuesWithNegative(
        mergedProperties,
        "cash-restricted"
      );
      formula[index].shareholderLoans = sumPropertyValuesWithNegative(
        mergedProperties,
        "shareholder-loans"
      );
      formula[index].totalLiabilityAndNetworth = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-liability-net-worth"
      );
      formula[index].assetsWrittenOff = sumPropertyValuesWithNegative(
        mergedProperties,
        "assets-written-off"
      );
      formula[index].loanPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "loan-payable"
      );
      formula[index].billsPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "bills-payable"
      );
      formula[index].accountsPayableProgress = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-payable-progress"
      );
      formula[index].accountsPayableRetention = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-payable-retention"
      );
      formula[index].otherPayables = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-payables"
      );
      formula[index].deferredRevenue = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-revenue"
      );
      formula[index].currentPortionLongTermDebt = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-portion-long-term-debt"
      );
      formula[index].currentLeaseLiability = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-lease-liability"
      );
      formula[index].employeeLoans = sumPropertyValuesWithNegative(
        mergedProperties,
        "employee-loans"
      );
      formula[index].currentTaxes = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-taxes"
      );
      formula[index].deferredTaxes = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-taxes"
      );
      formula[index].loansFromShareholders = sumPropertyValuesWithNegative(
        mergedProperties,
        "loans-from-shareholders"
      );
      formula[index].accruedExpenses = sumPropertyValuesWithNegative(
        mergedProperties,
        "accrued-expenses"
      );
      formula[index].totalCurrentLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-current-liabilities"
      );
      formula[index].longTermLoanPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-loan-payable"
      );
      formula[index].longTermLeaseLiability = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-lease-liability"
      );
      formula[index].longTermDebtOther = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-debt-other"
      );
      formula[index].longTermDeferredTax = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-deferred-tax"
      );
      formula[index].otherLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-liabilities"
      );
      formula[index].minorityInterest = sumPropertyValuesWithNegative(
        mergedProperties,
        "minority-interest"
      );
      formula[index].totalLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-liabilities"
      );
      formula[index].subordinatedDebt = sumPropertyValuesWithNegative(
        mergedProperties,
        "subordinated-debt"
      );
      formula[index].shareCapital = sumPropertyValuesWithNegative(
        mergedProperties,
        "share-capital"
      );
      formula[index].intangibleAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "intangible-assets"
      );
      formula[index].retainedEarnings = sumPropertyValuesWithNegative(
        mergedProperties,
        "retained-earnings"
      );
      formula[index].netWorth = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-worth"
      );
      formula[index].otherEquity = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-equity"
      );
    });

    for (const year of formula) {
      const totalAssets = parseInt(year.totalAssets.replace(/,/g, ""));

      const totalLiabilityAndNetworth = parseInt(
        year.totalLiabilityAndNetworth.replace(/,/g, "")
      );

      const sumA =
        sanitizeValue(year.cash) +
        sanitizeValue(year.marketableSecurities) +
        sanitizeValue(year.accountsRecievableProgress) +
        sanitizeValue(year.accountsRecievableRetention) +
        sanitizeValue(year.defferedAssets) +
        sanitizeValue(year.inventory) +
        sanitizeValue(year.prepaidExpensesCurrent) +
        sanitizeValue(year.otherCurrentAssets) +
        sanitizeValue(year.prepaidExpensesNonCurrent) +
        sanitizeValue(year.accountsRecievableOverNinetyDays) +
        sanitizeValue(year.plantsAndEquipment) +
        sanitizeValue(year.machineryAndVehicles) +
        sanitizeValue(year.furnitureAndFixture) +
        sanitizeValue(year.realEstate) +
        sanitizeValue(year.otherFixedAssets) +
        -parseInt(year.accumulatedDepreciation) +
        sanitizeValue(year.otherNonCurrentAssets) +
        sanitizeValue(year.assetsOnLease) +
        sanitizeValue(year.cashRestricted) +
        sanitizeValue(year.shareholderLoans) +
        sanitizeValue(year.intangibleAssets) +
        sanitizeValue(year.assetsWrittenOff);

      if (totalAssets != sumA) {
        labelsToValidate["balanceSheet"]["total-assets"].push({
          year: year.reportingYear,
          hasFailed: true,
          message: `Year ${
            year.reportingYear
          }. The sum of all assets (${sumA.toLocaleString()}) does not equal Total Assets (${totalAssets.toLocaleString()}) as read from the document.`,
        });
      } else {
        labelsToValidate["balanceSheet"]["total-assets"].push({
          year: year.reportingYear,
          hasFailed: false,
          message: `Year ${
            year.reportingYear
          }. The sum of all assets (${sumA.toLocaleString()}) equals Total Assets (${totalAssets.toLocaleString()}) as read from the document.`,
        });
      }

      const sumC =
        sanitizeValue(year.loanPayable) +
        sanitizeValue(year.billsPayable) +
        sanitizeValue(year.accountsPayableProgress) +
        sanitizeValue(year.accountsPayableRetention) +
        sanitizeValue(year.otherPayables) +
        sanitizeValue(year.deferredRevenue) +
        sanitizeValue(year.currentPortionLongTermDebt) +
        sanitizeValue(year.currentLeaseLiability) +
        sanitizeValue(year.employeeLoans) +
        sanitizeValue(year.currentTaxes) +
        sanitizeValue(year.deferredTaxes) +
        sanitizeValue(year.loansFromShareholders) +
        sanitizeValue(year.accruedExpenses) +
        sanitizeValue(year.totalCurrentLiabilities) +
        sanitizeValue(year.longTermLoanPayable) +
        sanitizeValue(year.longTermLeaseLiability) +
        sanitizeValue(year.longTermDebtOther) +
        sanitizeValue(year.longTermDeferredTax) +
        sanitizeValue(year.otherLiabilities) +
        sanitizeValue(year.minorityInterest) +
        sanitizeValue(year.totalLiabilities) +
        sanitizeValue(year.subordinatedDebt) +
        sanitizeValue(year.shareCapital) +
        sanitizeValue(year.retainedEarnings) +
        sanitizeValue(year.otherEquity) +
        sanitizeValue(year.netWorth);

      const sumD =
        sanitizeValue(year.totalCurrentLiabilities) +
        sanitizeValue(year.totalLiabilities) +
        sanitizeValue(year.netWorth);

      if (totalLiabilityAndNetworth !== sumC - sumD) {
        labelsToValidate["balanceSheet"]["total-liability-net-worth"].push({
          year: year.reportingYear,
          hasFailed: true,
          message: `Year ${
            year.reportingYear
          }. The sum of all liabilities and net worth (${(
            sumC - sumD
          ).toLocaleString()}) does not equal Total Liabilities and Net worth (${totalLiabilityAndNetworth.toLocaleString()}) as read from the document.`,
        });
      } else {
        labelsToValidate["balanceSheet"]["total-liability-net-worth"].push({
          year: year.reportingYear,
          hasFailed: false,
          message: `Year ${
            year.reportingYear
          }. The sum of all liabilities and net worth (${(
            sumC - sumD
          ).toLocaleString()}) equals Total Liabilities and Net worth (${totalLiabilityAndNetworth.toLocaleString()}) as read from the document.`,
        });
      }
    }
  };
  const profitAndLoss = (splitFilesData) => {
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "profitAndLoss"
    );

    if (!matchedSplit) {
      return;
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return;
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        labelsToValidate["profitAndLoss"]["reporting-dates"].push({
          year: reportingDate,
          hasFailed: true,
          message: `Year ${reportingDate}. The reporting date format is not valid. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`,
        });
      } else {
        labelsToValidate["profitAndLoss"]["reporting-dates"].push({
          year: reportingDate,
          hasFailed: false,
          message: `Reporting date format validation passed: "${reportingDate}"`,
        });
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map(() => ({
      reportingYear: "",
      grossProfit: "",
      grossContractIncome: "",
      managementFeeIncome: "",
      otherOperatingExpense: "",
      intercompanyRevenue: "",
      wageExpense: "",
      subContractExpense: "",
      materialExpense: "",
      equipmentExpense: "",
      otherDirectExpense: "",
      explorationAndEvaluationExpense: "",
      intercompanyDirectExpense: "",
      operatingProfit: "",
      totalOverheadExpense: "",
      adminExpense: "",
      otherNonOperatingExpense: "",
      salaryAndBonus: "",
      depreciation: "",
      intercompanyOverheadExpense: "",
      netIncomeAfterTax: "",
      otherIncome: "",
      discretionaryBonus: "",
      otherOperatingIncome: "",
      interestExpense: "",
      incomeTaxCurrent: "",
      incomeTaxDeferred: "",
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          console.log("Entity.properties: ", entity.properties);
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties
      );

      formula[index].totalOperatingRevenue = sumPropertyValues(
        mergedProperties,
        "total-operating-revenue"
      );

      formula[index].totalDirectExpense = sumPropertyValues(
        mergedProperties,
        "total-direct-expense"
      );

      formula[index].reportingYear = sumPropertyValues(
        mergedProperties,
        "reporting-year"
      );
      formula[index].grossProfit = sumPropertyValues(
        mergedProperties,
        "gross-profit"
      );
      formula[index].grossContractIncome = sumPropertyValues(
        mergedProperties,
        "gross-contract-income"
      );
      formula[index].managementFeeIncome = sumPropertyValues(
        mergedProperties,
        "management-fee-income"
      );
      formula[index].otherOperatingExpense = sumPropertyValues(
        mergedProperties,
        "other-operating-expense"
      );
      formula[index].intercompanyRevenue = sumPropertyValues(
        mergedProperties,
        "intercompany-revenue"
      );
      formula[index].wageExpense = sumPropertyValues(
        mergedProperties,
        "wage-expense"
      );
      formula[index].subContractExpense = sumPropertyValues(
        mergedProperties,
        "subcontract-expense"
      );
      formula[index].materialExpense = sumPropertyValues(
        mergedProperties,
        "material-expense"
      );
      formula[index].equipmentExpense = sumPropertyValues(
        mergedProperties,
        "equipment-expense"
      );
      formula[index].otherDirectExpense = sumPropertyValues(
        mergedProperties,
        "other-direct-expenses"
      );
      formula[index].explorationAndEvaluationExpense = sumPropertyValues(
        mergedProperties,
        "exploration-and-evaluation-expense"
      );
      formula[index].intercompanyDirectExpense = sumPropertyValues(
        mergedProperties,
        "intercompany-direct-expense"
      );
      formula[index].operatingProfit = sumPropertyValues(
        mergedProperties,
        "operating-profit"
      );
      formula[index].totalOverheadExpense = sumPropertyValues(
        mergedProperties,
        "total-overhead-expense"
      );
      formula[index].adminExpense = sumPropertyValues(
        mergedProperties,
        "admin-expense"
      );
      formula[index].otherNonOperatingExpense = sumPropertyValues(
        mergedProperties,
        "other-non-operating-expense"
      );
      formula[index].salaryAndBonus = sumPropertyValues(
        mergedProperties,
        "salary-and-bonus"
      );
      formula[index].depreciation = sumPropertyValues(
        mergedProperties,
        "depreciation"
      );
      formula[index].intercompanyOverheadExpense = sumPropertyValues(
        mergedProperties,
        "intercompany-overhead-expense"
      );
      formula[index].netIncomeAfterTax = sumPropertyValues(
        mergedProperties,
        "net-income-after-tax"
      );
      formula[index].otherIncome = sumPropertyValues(
        mergedProperties,
        "other-income"
      );
      formula[index].discretionaryBonus = sumPropertyValues(
        mergedProperties,
        "discretionary-bonus"
      );
      formula[index].otherOperatingIncome = sumPropertyValues(
        mergedProperties,
        "other-operating-income"
      );
      formula[index].interestExpense = sumPropertyValues(
        mergedProperties,
        "interest-expense"
      );
      formula[index].incomeTaxCurrent = sumPropertyValues(
        mergedProperties,
        "income-tax-current"
      );
      formula[index].incomeTaxDeferred = sumPropertyValues(
        mergedProperties,
        "income-tax-deferred"
      );
    });

    for (const year of formula) {
      const calculatedTotalOperatingRevenue =
        Math.abs(
          parseInt(year.totalOperatingRevenue.replace(/[\s,()]/g, ""))
        ) ||
        Math.abs(parseInt(year.grossContractIncome.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.managementFeeIncome.replace(/[\s,()]/g, ""))) +
          Math.abs(
            parseInt(year.otherOperatingIncome.replace(/[\s,()]/g, ""))
          ) +
          Math.abs(parseInt(year.intercompanyRevenue.replace(/[\s,()]/g, "")));

      const calculatedTotalDirectExpense =
        Math.abs(parseInt(year.totalDirectExpense.replace(/[\s,()]/g, ""))) ||
        Math.abs(parseInt(year.wageExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.subContractExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.materialExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.equipmentExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.otherDirectExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(
            parseInt(
              year.explorationAndEvaluationExpense.replace(/[\s,()]/g, "")
            )
          ) +
          Math.abs(
            parseInt(year.intercompanyDirectExpense.replace(/[\s,()]/g, ""))
          );

      const calculatedGrossProfit =
        calculatedTotalOperatingRevenue - calculatedTotalDirectExpense;

      if (
        calculatedGrossProfit !==
        Math.abs(parseInt(year.grossProfit.replace(/[\s,()]/g, "")))
      ) {
        labelsToValidate["profitAndLoss"]["gross-profit"].push({
          year: year.reportingYear,
          hasFailed: true,
          message: `Year ${
            year.reportingYear
          }. The sum of all Income items less the sum of all Direct Expense items (${calculatedGrossProfit.toLocaleString()}) does not equal Gross Profit (${parseInt(
            year.grossProfit.replace(/,/g, "")
          ).toLocaleString()}) as read from the document.`,
        });
      } else {
        labelsToValidate["profitAndLoss"]["gross-profit"].push({
          year: year.reportingYear,
          hasFailed: false,
          message: `Year ${
            year.reportingYear
          }. The sum of all Income items less the sum of all Direct Expense items (${calculatedGrossProfit.toLocaleString()}) equals Gross Profit (${parseInt(
            year.grossProfit.replace(/,/g, "")
          ).toLocaleString()}) as read from the document.`,
        });
      }

      const totalOverheadExpense = parseInt(
        year.totalOverheadExpense.replace(/[\s,()]/g, "")
      );
      const operatingProfit = parseInt(
        year.operatingProfit.replace(/[\s,()]/g, "")
      );
      const grossProfit = parseInt(year.grossProfit.replace(/[\s,()]/g, ""));

      const adminExpense = parseInt(year.adminExpense.replace(/[\s,()]/g, ""));
      const otherNonOperatingExpense = parseInt(
        year.otherNonOperatingExpense.replace(/[\s,()]/g, "")
      );
      const salaryAndBonus = parseInt(
        year.salaryAndBonus.replace(/[\s,()]/g, "")
      );
      const depreciation = parseInt(year.depreciation.replace(/[\s,()]/g, ""));
      const intercompanyOverheadExpense = parseInt(
        year.intercompanyOverheadExpense.replace(/[\s,()]/g, "")
      );
      const otherIncome = parseInt(year.otherIncome.replace(/[\s,()]/g, ""));
      const netIncomeAfterTax = parseInt(
        year.netIncomeAfterTax.replace(/[\s,()]/g, "")
      );
      const discretionaryBonus = parseInt(
        year.discretionaryBonus.replace(/[\s,()]/g, "")
      );

      const sumA2 =
        Math.abs(grossProfit) -
        (Math.abs(adminExpense) +
          Math.abs(otherNonOperatingExpense) +
          Math.abs(salaryAndBonus) +
          Math.abs(depreciation) +
          Math.abs(intercompanyOverheadExpense));

      const sumA3 =
        Math.abs(totalOverheadExpense) ||
        Math.abs(adminExpense) +
          Math.abs(otherNonOperatingExpense) +
          Math.abs(salaryAndBonus) +
          Math.abs(depreciation) +
          Math.abs(intercompanyOverheadExpense);

      if (totalOverheadExpense != 0) {
        if (grossProfit - sumA3 !== operatingProfit) {
          labelsToValidate["profitAndLoss"]["operating-profit"].push({
            year: year.reportingYear,
            hasFailed: true,
            message: `Year ${
              year.reportingYear
            }. Gross Profit (less) Total Overhead Expense (${(
              grossProfit - totalOverheadExpense
            ).toLocaleString()}) does not equal Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`,
          });
        } else {
          labelsToValidate["profitAndLoss"]["operating-profit"].push({
            year: year.reportingYear,
            hasFailed: false,
            message: `Year ${
              year.reportingYear
            }. Gross Profit (less) Total Overhead Expense (${(
              grossProfit - totalOverheadExpense
            ).toLocaleString()}) equals Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`,
          });
        }
      } else {
        if (sumA2 !== operatingProfit) {
          labelsToValidate["profitAndLoss"]["operating-profit"].push({
            year: year.reportingYear,
            hasFailed: true,
            message: `Year ${
              year.reportingYear
            }. Gross Profit (less) all Indirect Expenses (${sumA2.toLocaleString()}) does not equal Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`,
          });
        } else {
          labelsToValidate["profitAndLoss"]["operating-profit"].push({
            year: year.reportingYear,
            hasFailed: false,
            message: `Year ${
              year.reportingYear
            }. Gross Profit (less) all Indirect Expenses (${sumA2.toLocaleString()}) equals Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`,
          });
        }
      }

      const interestExpense = parseInt(
        year.interestExpense.replace(/[\s,()]/g, "")
      );

      const incomeTaxCurrent = parseInt(
        year.incomeTaxCurrent.replace(/[\s,()]/g, "")
      );

      const incomeTaxDeferred = parseInt(
        year.incomeTaxDeferred.replace(/[\s,()]/g, "")
      );

      const result =
        operatingProfit +
        otherIncome -
        (discretionaryBonus +
          interestExpense +
          incomeTaxCurrent +
          incomeTaxDeferred);

      if (operatingProfit !== 0) {
        if (netIncomeAfterTax !== result) {
          labelsToValidate["profitAndLoss"]["net-income-after-tax"].push({
            year: year.reportingYear,
            hasFailed: true,
            message: `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) does not match the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`,
          });
        } else {
          labelsToValidate["profitAndLoss"]["net-income-after-tax"].push({
            year: year.reportingYear,
            hasFailed: false,
            message: `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) matches the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`,
          });
        }
      } else {
        if (
          netIncomeAfterTax !==
          sumA2 +
            otherIncome -
            (discretionaryBonus +
              interestExpense +
              incomeTaxCurrent +
              incomeTaxDeferred)
        ) {
          labelsToValidate["profitAndLoss"]["net-income-after-tax"].push({
            year: year.reportingYear,
            hasFailed: true,
            message: `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) does not match the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`,
          });
        } else {
          labelsToValidate["profitAndLoss"]["net-income-after-tax"].push({
            year: year.reportingYear,
            hasFailed: false,
            message: `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) matches the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`,
          });
        }
      }
    }

    const workInProgressSplits = splitFilesData.filter(
      (data) => (data.splitFiles?.[0]?.labellerModel ?? "") === "workInProgress"
    );

    const workCompletedSplits = splitFilesData.filter(
      (data) => (data.splitFiles?.[0]?.labellerModel ?? "") === "workCompleted"
    );

    if (workInProgressSplits?.length > 0 || workCompletedSplits?.length > 0) {
      const maxYear = Math.max(
        ...getUniqueReportingYears(
          matchedSplit.splitFiles[0].labellerOutput.entities
        ).map((year) => parseInt(year))
      ).toString();

      const maxYearProperty =
        matchedSplit.splitFiles[0].labellerOutput.entities.find((entity) => {
          return entity.properties?.some(
            (property) =>
              property.type === "reporting-year" &&
              property.mentionText === maxYear
          );
        });

      const maxYearGrossContractIncome = maxYearProperty?.properties?.find(
        (prop) => prop.type === "gross-contract-income"
      )?.mentionText;

      if (maxYearGrossContractIncome) {
        let currentYearEarnedRevenueSum = 0;

        const wipEntities = workInProgressSplits
          .flatMap((data) => {
            const asOfDate = data.splitFiles[0].labellerOutput.entities.find(
              (e) => e.type === "as_of_date" && e.mentionText.includes(maxYear)
            );
            if (asOfDate) {
              return data.splitFiles[0].labellerOutput.entities;
            }
            return null;
          })
          .filter((data) => data !== null);

        const workCompletedEntities = workCompletedSplits
          .flatMap((data) => {
            const asOfDate = data.splitFiles[0].labellerOutput.entities.find(
              (e) => e.type === "as_of_date" && e.mentionText.includes(maxYear)
            );
            if (asOfDate) {
              return data.splitFiles[0].labellerOutput.entities;
            }
            return null;
          })
          .filter((data) => data !== null);

        const wipAndWorkCompletedEntities = [
          ...workCompletedEntities,
          ...wipEntities,
        ];

        if (wipAndWorkCompletedEntities.length > 0) {
          for (const entity of wipAndWorkCompletedEntities) {
            if (entity.type === "project") {
              const currentYearEarnedRevenue =
                entity.properties?.find(
                  (prop) => prop.type === "current_year_earned_revenue"
                )?.mentionText || "0";

              console.log(
                "current year earned revenue ",
                currentYearEarnedRevenue
              );

              if (currentYearEarnedRevenue) {
                currentYearEarnedRevenueSum += parseInt(
                  currentYearEarnedRevenue.replace(/[\s,()]/g, "")
                );
              }
            }
          }

          if (
            currentYearEarnedRevenueSum !=
            parseInt(maxYearGrossContractIncome.replace(/[\s,()]/g, ""))
          ) {
            labelsToValidate["profitAndLoss"]["gross-contract-income"].push({
              year: maxYear,
              hasFailed: true,
              message: `The sum of current year earned revenue ${currentYearEarnedRevenueSum.toLocaleString()} as calculated from WIP and Work Completed documents does not equal the value of Gross Contract Income ${maxYearGrossContractIncome.toLocaleString()} as read from the Profit and Loss Statement`,
            });
          } else {
            labelsToValidate["profitAndLoss"]["gross-contract-income"].push({
              year: maxYear,
              hasFailed: false,
              message: `The sum of current year earned revenue ${currentYearEarnedRevenueSum.toLocaleString()} as calculated from WIP and Work Completed documents equals the value of Gross Contract Income ${maxYearGrossContractIncome.toLocaleString()} as read from the Profit and Loss Statement`,
            });
          }
        }
      }
    }
  };
  const cashflow = (splitFilesData) => {
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "cashflow"
    );

    if (!matchedSplit) {
      return;
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return;
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        labelsToValidate["cashflow"]["reporting-dates"].push({
          hasFailed: true,
          message: `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`,
        });
      } else {
        labelsToValidate["cashflow"]["reporting-dates"].push({
          hasFailed: false,
          message: `Reporting date format validation passed: "${reportingDate}"`,
        });
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map((year) => ({
      reportingYear: year,
      netIncome: "",
      depreciationCf: "",
      badDebtExpense: "",
      otherNonCash: "",
      incomeTaxDeferredCf: "",
      changeInAssets: "",
      changeInLiabilities: "",
      netCashOperating: "",
      netCashInvesting: "",
      netCashFinancing: "",
      netCashFlow: "",
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties ?? []
      );

      formula[index].netIncome = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-income"
      );
      formula[index].depreciationCf = sumPropertyValuesWithNegative(
        mergedProperties,
        "depreciation-cf"
      );
      formula[index].badDebtExpense = sumPropertyValuesWithNegative(
        mergedProperties,
        "bad-debt-expense"
      );
      formula[index].otherNonCash = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-non-cash"
      );
      formula[index].incomeTaxDeferredCf = sumPropertyValuesWithNegative(
        mergedProperties,
        "income-tax-deferred-cf"
      );
      formula[index].changeInAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "change-in-assets"
      );
      formula[index].changeInLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "change-in-liabilities"
      );
      formula[index].netCashOperating = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-operating"
      );
      formula[index].netCashInvesting = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-investing"
      );
      formula[index].netCashFinancing = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-financing"
      );
      formula[index].netCashFlow = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-flow"
      );
    });

    for (const year of formula) {
      const netCashFlow = parseInt(year.netCashFlow.replace(/[\s,()]/g, ""));

      const netCashOperating = parseInt(
        year.netCashOperating.replace(/[\s,()]/g, "")
      );

      const netIncome = parseInt(year.netIncome);
      const depreciationCf = parseInt(year.depreciationCf);
      const badDebtExpense = parseInt(year.badDebtExpense);
      const otherNonCash = parseInt(year.otherNonCash);
      const incomeTaxDeferredCf = parseInt(year.incomeTaxDeferredCf);
      const changeInAssets = parseInt(year.changeInAssets);
      const changeInLiabilities = parseInt(year.changeInLiabilities);

      const sumA =
        netIncome +
        depreciationCf +
        badDebtExpense +
        otherNonCash +
        incomeTaxDeferredCf +
        changeInAssets +
        changeInLiabilities;

      if (netCashOperating !== sumA) {
        labelsToValidate["cashflow"]["net-cash-operating"].push({
          year: year.reportingYear,
          hasFailed: true,
          message: `Year ${
            year.reportingYear
          }. Net Cash from Operating Activities (${netCashOperating.toLocaleString()}) does not match the calculated sum (${sumA.toLocaleString()}). Please review the components of Net Income, Depreciation, Bad Debt Expense, Other Non-Cash Items, Deferred Income Tax, and changes in Assets and Liabilities.`,
        });
      } else {
        labelsToValidate["cashflow"]["net-cash-operating"].push({
          year: year.reportingYear,
          hasFailed: false,
          message: `Year ${
            year.reportingYear
          }. Net Cash from Operating Activities (${netCashOperating.toLocaleString()}) matches the calculated sum (${sumA.toLocaleString()}). Please review the components of Net Income, Depreciation, Bad Debt Expense, Other Non-Cash Items, Deferred Income Tax, and changes in Assets and Liabilities.`,
        });
      }

      const sumB =
        parseInt(year.netCashOperating) +
        parseInt(year.netCashInvesting) +
        parseInt(year.netCashFinancing);

      if (netCashFlow != sumB) {
        labelsToValidate["cashflow"]["net-cash-flow"].push({
          year: year.reportingYear,
          hasFailed: true,
          message: `Year ${
            year.reportingYear
          }. Net Cash Flow (${netCashFlow.toLocaleString()}) does not match the calculated sum (${sumB.toLocaleString()}). Please review the components of Net Cash from Operating, Investing, and Financing Activities.`,
        });
      } else {
        labelsToValidate["cashflow"]["net-cash-flow"].push({
          year: year.reportingYear,
          hasFailed: false,
          message: `Year ${
            year.reportingYear
          }. Net Cash Flow (${netCashFlow.toLocaleString()}) matches the calculated sum (${sumB.toLocaleString()}). Please review the components of Net Cash from Operating, Investing, and Financing Activities.`,
        });
      }
    }
  };
  const workInProgress = (splitFilesData) => {
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "workInProgress"
    );

    if (!matchedSplit) {
      return;
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return;
    }

    const asOfDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "as_of_date"
      )?.mentionText ?? "";

    if (asOfDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(asOfDate)) {
        labelsToValidate["workInProgress"]["as_of_date"].push({
          hasFailed: true,
          message: `Invalid as of date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${asOfDate}" (will prevent transfer data if not corrected)`,
        });
      } else {
        labelsToValidate["workInProgress"]["as_of_date"].push({
          hasFailed: false,
          message: `As of date format validation passed: "${asOfDate}"`,
        });
      }
    }

    let formula = matchedSplit.splitFiles[0].labellerOutput.entities
      .filter((entity) => entity.type === "project")
      .map((entity) => {
        console.log("Wip entity.propertuies ", entity.properties);

        const projectName =
          entity.properties?.find((prop) => prop.type === "project_name")
            ?.mentionText || "Unknown Project";
        const estimatedGrossProfit = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "estimated-gross-profit")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        const estimatedCostOfRevenue = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "estimated_cost_of_revenue")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        const projectValueOrEstimatedRevenue = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "project_value_or_estimated_revenue")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        return {
          projectName,
          estimatedGrossProfit,
          estimatedCostOfRevenue,
          projectValueOrEstimatedRevenue,
        };
      });

    for (const project of formula) {
      if (
        !labelsToValidate["workInProgress"]["projectSpecific"][
          project.projectName
        ]
      ) {
        labelsToValidate["workInProgress"]["projectSpecific"][
          project.projectName
        ] = [];
      }
      if (
        !project.estimatedGrossProfit &&
        !project.projectValueOrEstimatedRevenue &&
        !project.estimatedCostOfRevenue
      ) {
        labelsToValidate["workInProgress"]["projectSpecific"][
          project.projectName
        ].push({
          message: `Project ${project.projectName}. Estimated Gross Profit, Estimated Revenue and Estimated Cost of Revenue are missing.`,
          hasFailed: true,
        });
      } else {
        labelsToValidate["workInProgress"]["projectSpecific"][
          project.projectName
        ].push({
          message: `Project ${project.projectName}. Estimated Gross Profit, Estimated Revenue and Estimated Cost of Revenue are present.`,
          hasFailed: false,
        });
      }
    }
  };

  const workCompleted = (splitFilesData) => {
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "workCompleted"
    );

    if (!matchedSplit) {
      return;
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return;
    }

    const asOfDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "as_of_date"
      )?.mentionText ?? "";

    if (asOfDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(asOfDate)) {
        labelsToValidate["workCompleted"]["as_of_date"].push({
          hasFailed: true,
          message: `Invalid as of date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${asOfDate}" (will prevent transfer data if not corrected)`,
        });
      } else {
        labelsToValidate["workCompleted"]["as_of_date"].push({
          hasFailed: false,
          message: `As of date format validation passed: "${asOfDate}"`,
        });
      }
    }
  };

  balanceSheet(splitFilesData);
  profitAndLoss(splitFilesData);
  cashflow(splitFilesData);
  workInProgress(splitFilesData);
  workCompleted(splitFilesData);

  return labelsToValidate;
};

/**
 * Validation utility for Parser Screen
 *
 * Provides multiple validation methods for verifying the accuracy of financial data
 * across various financial statements such as Balance Sheet, Profit and Loss,
 * Cash Flow, Work in Progress, and Work Completed.
 *
 * @typedef {Object} ValidationResult
 * @property {boolean} succeeded - Indicates whether the validation passed (`true`) or failed (`false`).
 * @property {string} message - Provides a detailed message explaining the validation result or the reason for failure.
 *
 * @type {Object}
 * @property {function(Object): ValidationResult} balanceSheet - Validates the balance sheet data.
 * @property {function(Object, function): ValidationResult} profitAndLoss - Validates the profit and loss data.
 * @property {function(Object): ValidationResult} cashflow - Validates the cash flow data.
 * @property {function(Object): ValidationResult} workInProgress - Validates work-in-progress data.
 * @property {function(Object): ValidationResult} workCompleted - Validates work-completed data.
 */
export const isValidationSuccessful = {
  balanceSheet: (splitFilesData) => {
    console.log("called val for balance sheet");

    let failed = [];
    let succeeded = [];

    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "balanceSheet"
    );

    if (!matchedSplit) {
      return { succeeded: true };
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        failed.push(
          `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`
        );
      } else {
        succeeded.push(
          `Reporting date format validation passed: "${reportingDate}"`
        );
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map((year) => ({
      reportingYear: year,
      totalAssets: "",
      cash: "",
      marketableSecurities: "",
      accountsRecievableProgress: "",
      accountsRecievableRetention: "",
      defferedAssets: "",
      inventory: "",
      prepaidExpensesCurrent: "",
      otherCurrentAssets: "",
      totalCurrentAssets: "",
      prepaidExpensesNonCurrent: "",
      accountsRecievableOverNinetyDays: "",
      plantsAndEquipment: "",
      machineryAndVehicles: "",
      furnitureAndFixture: "",
      realEstate: "",
      otherFixedAssets: "",
      accumulatedDepreciation: "",
      otherNonCurrentAssets: "",
      assetsOnLease: "",
      cashRestricted: "",
      shareholderLoans: "",
      totalLiabilityAndNetworth: "",
      assetsWrittenOff: "",
      loanPayable: "",
      billsPayable: "",
      accountsPayableProgress: "",
      accountsPayableRetention: "",
      otherPayables: "",
      deferredRevenue: "",
      currentPortionLongTermDebt: "",
      currentLeaseLiability: "",
      employeeLoans: "",
      currentTaxes: "",
      deferredTaxes: "",
      loansFromShareholders: "",
      accruedExpenses: "",
      totalCurrentLiabilities: "",
      longTermLoanPayable: "",
      longTermLeaseLiability: "",
      longTermDebtOther: "",
      longTermDeferredTax: "",
      otherLiabilities: "",
      minorityInterest: "",
      totalLiabilities: "",
      subordinatedDebt: "",
      shareCapital: "",
      intangibleAssets: "",
      retainedEarnings: "",
      netWorth: "",
      otherEquity: "",
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties
      );

      formula[index].cash = sumPropertyValuesWithNegative(
        mergedProperties,
        "cash"
      );
      formula[index].totalAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-assets"
      );
      formula[index].marketableSecurities = sumPropertyValuesWithNegative(
        mergedProperties,
        "marketable-securities"
      );
      formula[index].accountsRecievableProgress = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-receivable-progress"
      );
      formula[index].accountsRecievableRetention =
        sumPropertyValuesWithNegative(
          mergedProperties,
          "accounts-receivable-retention"
        );
      formula[index].defferedAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-assets"
      );
      formula[index].inventory = sumPropertyValuesWithNegative(
        mergedProperties,
        "inventory"
      );
      formula[index].prepaidExpensesCurrent = sumPropertyValuesWithNegative(
        mergedProperties,
        "prepaid-expenses-current"
      );
      formula[index].otherCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-current-assets"
      );
      formula[index].totalCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-current-assets"
      );
      formula[index].prepaidExpensesNonCurrent = sumPropertyValuesWithNegative(
        mergedProperties,
        "prepaid-expenses-non-current"
      );
      formula[index].accountsRecievableOverNinetyDays =
        sumPropertyValuesWithNegative(
          mergedProperties,
          "accounts-receivable-over-90days"
        );
      formula[index].plantsAndEquipment = sumPropertyValuesWithNegative(
        mergedProperties,
        "plant-and-equipment"
      );
      formula[index].machineryAndVehicles = sumPropertyValuesWithNegative(
        mergedProperties,
        "machinery-and-vehicles"
      );
      formula[index].furnitureAndFixture = sumPropertyValuesWithNegative(
        mergedProperties,
        "furniture-and-fixture"
      );
      formula[index].realEstate = sumPropertyValuesWithNegative(
        mergedProperties,
        "real-estate"
      );
      formula[index].otherFixedAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-fixed-assets"
      );
      formula[index].accumulatedDepreciation = sumPropertyValues(
        mergedProperties,
        "accumulated-depreciation"
      );
      formula[index].otherNonCurrentAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-non-current-assets"
      );
      formula[index].assetsOnLease = sumPropertyValuesWithNegative(
        mergedProperties,
        "assets-on-lease"
      );
      formula[index].cashRestricted = sumPropertyValuesWithNegative(
        mergedProperties,
        "cash-restricted"
      );
      formula[index].shareholderLoans = sumPropertyValuesWithNegative(
        mergedProperties,
        "shareholder-loans"
      );
      formula[index].totalLiabilityAndNetworth = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-liability-net-worth"
      );
      formula[index].assetsWrittenOff = sumPropertyValuesWithNegative(
        mergedProperties,
        "assets-written-off"
      );
      formula[index].loanPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "loan-payable"
      );
      formula[index].billsPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "bills-payable"
      );
      formula[index].accountsPayableProgress = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-payable-progress"
      );
      formula[index].accountsPayableRetention = sumPropertyValuesWithNegative(
        mergedProperties,
        "accounts-payable-retention"
      );
      formula[index].otherPayables = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-payables"
      );
      formula[index].deferredRevenue = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-revenue"
      );
      formula[index].currentPortionLongTermDebt = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-portion-long-term-debt"
      );
      formula[index].currentLeaseLiability = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-lease-liability"
      );
      formula[index].employeeLoans = sumPropertyValuesWithNegative(
        mergedProperties,
        "employee-loans"
      );
      formula[index].currentTaxes = sumPropertyValuesWithNegative(
        mergedProperties,
        "current-taxes"
      );
      formula[index].deferredTaxes = sumPropertyValuesWithNegative(
        mergedProperties,
        "deferred-taxes"
      );
      formula[index].loansFromShareholders = sumPropertyValuesWithNegative(
        mergedProperties,
        "loans-from-shareholders"
      );
      formula[index].accruedExpenses = sumPropertyValuesWithNegative(
        mergedProperties,
        "accrued-expenses"
      );
      formula[index].totalCurrentLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-current-liabilities"
      );
      formula[index].longTermLoanPayable = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-loan-payable"
      );
      formula[index].longTermLeaseLiability = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-lease-liability"
      );
      formula[index].longTermDebtOther = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-debt-other"
      );
      formula[index].longTermDeferredTax = sumPropertyValuesWithNegative(
        mergedProperties,
        "long-term-deferred-tax"
      );
      formula[index].otherLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-liabilities"
      );
      formula[index].minorityInterest = sumPropertyValuesWithNegative(
        mergedProperties,
        "minority-interest"
      );
      formula[index].totalLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "total-liabilities"
      );
      formula[index].subordinatedDebt = sumPropertyValuesWithNegative(
        mergedProperties,
        "subordinated-debt"
      );
      formula[index].shareCapital = sumPropertyValuesWithNegative(
        mergedProperties,
        "share-capital"
      );
      formula[index].intangibleAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "intangible-assets"
      );
      formula[index].retainedEarnings = sumPropertyValuesWithNegative(
        mergedProperties,
        "retained-earnings"
      );
      formula[index].netWorth = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-worth"
      );
      formula[index].otherEquity = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-equity"
      );
    });

    for (const year of formula) {
      const totalAssets = parseInt(year.totalAssets.replace(/,/g, ""));

      const totalLiabilityAndNetworth = parseInt(
        year.totalLiabilityAndNetworth.replace(/,/g, "")
      );

      const sumA =
        sanitizeValue(year.cash) +
        sanitizeValue(year.marketableSecurities) +
        sanitizeValue(year.accountsRecievableProgress) +
        sanitizeValue(year.accountsRecievableRetention) +
        sanitizeValue(year.defferedAssets) +
        sanitizeValue(year.inventory) +
        sanitizeValue(year.prepaidExpensesCurrent) +
        sanitizeValue(year.otherCurrentAssets) +
        sanitizeValue(year.prepaidExpensesNonCurrent) +
        sanitizeValue(year.accountsRecievableOverNinetyDays) +
        sanitizeValue(year.plantsAndEquipment) +
        sanitizeValue(year.machineryAndVehicles) +
        sanitizeValue(year.furnitureAndFixture) +
        sanitizeValue(year.realEstate) +
        sanitizeValue(year.otherFixedAssets) +
        -parseInt(year.accumulatedDepreciation) +
        sanitizeValue(year.otherNonCurrentAssets) +
        sanitizeValue(year.assetsOnLease) +
        sanitizeValue(year.cashRestricted) +
        sanitizeValue(year.shareholderLoans) +
        sanitizeValue(year.intangibleAssets) +
        sanitizeValue(year.assetsWrittenOff);

      if (totalAssets != sumA) {
        failed.push(
          `Year ${
            year.reportingYear
          }. The sum of all assets (${sumA.toLocaleString()}) does equals Total Assets (${totalAssets.toLocaleString()}) as read from the document.`
        );
      } else {
        succeeded.push(
          `Year ${
            year.reportingYear
          }. The sum of all assets (${sumA.toLocaleString()}) does not equal Total Assets (${totalAssets.toLocaleString()}) as read from the document.`
        );
      }

      const sumC =
        sanitizeValue(year.loanPayable) +
        sanitizeValue(year.billsPayable) +
        sanitizeValue(year.accountsPayableProgress) +
        sanitizeValue(year.accountsPayableRetention) +
        sanitizeValue(year.otherPayables) +
        sanitizeValue(year.deferredRevenue) +
        sanitizeValue(year.currentPortionLongTermDebt) +
        sanitizeValue(year.currentLeaseLiability) +
        sanitizeValue(year.employeeLoans) +
        sanitizeValue(year.currentTaxes) +
        sanitizeValue(year.deferredTaxes) +
        sanitizeValue(year.loansFromShareholders) +
        sanitizeValue(year.accruedExpenses) +
        sanitizeValue(year.totalCurrentLiabilities) +
        sanitizeValue(year.longTermLoanPayable) +
        sanitizeValue(year.longTermLeaseLiability) +
        sanitizeValue(year.longTermDebtOther) +
        sanitizeValue(year.longTermDeferredTax) +
        sanitizeValue(year.otherLiabilities) +
        sanitizeValue(year.minorityInterest) +
        sanitizeValue(year.totalLiabilities) +
        sanitizeValue(year.subordinatedDebt) +
        sanitizeValue(year.shareCapital) +
        sanitizeValue(year.retainedEarnings) +
        sanitizeValue(year.otherEquity) +
        sanitizeValue(year.netWorth);

      const sumD =
        sanitizeValue(year.totalCurrentLiabilities) +
        sanitizeValue(year.totalLiabilities) +
        sanitizeValue(year.netWorth);

      if (totalLiabilityAndNetworth !== sumC - sumD) {
        failed.push(
          `Year ${
            year.reportingYear
          }. The sum of all liabilities and net worth (${(
            sumC - sumD
          ).toLocaleString()}) does not equal Total Liabilities and Net worth (${totalLiabilityAndNetworth.toLocaleString()}) as read from the document.`
        );
      } else {
        succeeded.push(
          `Year ${
            year.reportingYear
          }. The sum of all liabilities and net worth (${(
            sumC - sumD
          ).toLocaleString()}) equals Total Liabilities and Net worth (${totalLiabilityAndNetworth.toLocaleString()}) as read from the document.`
        );
      }
    }

    if (failed.length > 0) {
      return {
        succeeded: false,
        failedMessages: failed,
        succeededMessages: succeeded,
      };
    }

    return {
      succeeded: true,
      failedMessages: failed,
      succeededMessages: succeeded,
    };
  },
  profitAndLoss: (splitFilesData) => {
    let failed = [],
      succeeded = [];
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "profitAndLoss"
    );

    if (!matchedSplit) {
      return { succeeded: true };
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        failed.push(
          `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`
        );
      } else {
        succeeded.push(
          `Reporting date format validation passed: "${reportingDate}"`
        );
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map(() => ({
      reportingYear: "",
      grossProfit: "",
      grossContractIncome: "",
      managementFeeIncome: "",
      otherOperatingExpense: "",
      intercompanyRevenue: "",
      wageExpense: "",
      subContractExpense: "",
      materialExpense: "",
      equipmentExpense: "",
      otherDirectExpense: "",
      explorationAndEvaluationExpense: "",
      intercompanyDirectExpense: "",
      operatingProfit: "",
      totalOverheadExpense: "",
      adminExpense: "",
      otherNonOperatingExpense: "",
      salaryAndBonus: "",
      depreciation: "",
      intercompanyOverheadExpense: "",
      netIncomeAfterTax: "",
      otherIncome: "",
      discretionaryBonus: "",
      otherOperatingIncome: "",
      interestExpense: "",
      incomeTaxCurrent: "",
      incomeTaxDeferred: "",
      depreciationIs: ""
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          console.log("Entity.properties: ", entity.properties);
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties
      );

      formula[index].totalOperatingRevenue = sumPropertyValues(
        mergedProperties,
        "total-operating-revenue"
      );

      formula[index].totalDirectExpense = sumPropertyValues(
        mergedProperties,
        "total-direct-expense"
      );

      formula[index].reportingYear = sumPropertyValues(
        mergedProperties,
        "reporting-year"
      );
      formula[index].grossProfit = sumPropertyValues(
        mergedProperties,
        "gross-profit"
      );
      formula[index].grossContractIncome = sumPropertyValues(
        mergedProperties,
        "gross-contract-income"
      );
      formula[index].managementFeeIncome = sumPropertyValues(
        mergedProperties,
        "management-fee-income"
      );
      formula[index].otherOperatingExpense = sumPropertyValues(
        mergedProperties,
        "other-operating-expense"
      );
      formula[index].intercompanyRevenue = sumPropertyValues(
        mergedProperties,
        "intercompany-revenue"
      );
      formula[index].wageExpense = sumPropertyValues(
        mergedProperties,
        "wage-expense"
      );
      formula[index].subContractExpense = sumPropertyValues(
        mergedProperties,
        "subcontract-expense"
      );
      formula[index].materialExpense = sumPropertyValues(
        mergedProperties,
        "material-expense"
      );
      formula[index].equipmentExpense = sumPropertyValues(
        mergedProperties,
        "equipment-expense"
      );
      formula[index].otherDirectExpense = sumPropertyValues(
        mergedProperties,
        "other-direct-expenses"
      );
      formula[index].explorationAndEvaluationExpense = sumPropertyValues(
        mergedProperties,
        "exploration-and-evaluation-expense"
      );
      formula[index].intercompanyDirectExpense = sumPropertyValues(
        mergedProperties,
        "intercompany-direct-expense"
      );
      formula[index].operatingProfit = sumPropertyValues(
        mergedProperties,
        "operating-profit"
      );
      formula[index].totalOverheadExpense = sumPropertyValues(
        mergedProperties,
        "total-overhead-expense"
      );
      formula[index].adminExpense = sumPropertyValues(
        mergedProperties,
        "admin-expense"
      );
      formula[index].otherNonOperatingExpense = sumPropertyValues(
        mergedProperties,
        "other-non-operating-expense"
      );
      formula[index].salaryAndBonus = sumPropertyValues(
        mergedProperties,
        "salary-and-bonus"
      );
      formula[index].depreciation = sumPropertyValues(
        mergedProperties,
        "depreciation-is"
      ); 

      formula[index].intercompanyOverheadExpense = sumPropertyValues(
        mergedProperties,
        "intercompany-overhead-expense"
      );
      formula[index].netIncomeAfterTax = sumPropertyValues(
        mergedProperties,
        "net-income-after-tax"
      );
      formula[index].otherIncome = sumPropertyValues(
        mergedProperties,
        "other-income"
      );
      formula[index].discretionaryBonus = sumPropertyValues(
        mergedProperties,
        "discretionary-bonus"
      );
      formula[index].otherOperatingIncome = sumPropertyValues(
        mergedProperties,
        "other-operating-income"
      );
      formula[index].interestExpense = sumPropertyValues(
        mergedProperties,
        "interest-expense"
      );
      formula[index].incomeTaxCurrent = sumPropertyValues(
        mergedProperties,
        "income-tax-current"
      );
      formula[index].incomeTaxDeferred = sumPropertyValues(
        mergedProperties,
        "income-tax-deferred"
      );
    });

    console.log("formula p&l", formula);

    // Validations
    for (const year of formula) {
      const calculatedTotalOperatingRevenue =
        Math.abs(
          parseInt(year.totalOperatingRevenue.replace(/[\s,()]/g, ""))
        ) ||
        Math.abs(parseInt(year.grossContractIncome.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.managementFeeIncome.replace(/[\s,()]/g, ""))) +
          Math.abs(
            parseInt(year.otherOperatingIncome.replace(/[\s,()]/g, ""))
          ) +
          Math.abs(parseInt(year.intercompanyRevenue.replace(/[\s,()]/g, "")));

      const calculatedTotalDirectExpense =
        Math.abs(parseInt(year.totalDirectExpense.replace(/[\s,()]/g, ""))) ||
        Math.abs(parseInt(year.wageExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.subContractExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.materialExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.equipmentExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(parseInt(year.otherDirectExpense.replace(/[\s,()]/g, ""))) +
          Math.abs(
            parseInt(
              year.explorationAndEvaluationExpense.replace(/[\s,()]/g, "")
            )
          ) +
          Math.abs(
            parseInt(year.intercompanyDirectExpense.replace(/[\s,()]/g, ""))
          );

      const calculatedGrossProfit =
        calculatedTotalOperatingRevenue - calculatedTotalDirectExpense;

      if (
        calculatedGrossProfit !==
        Math.abs(parseInt(year.grossProfit.replace(/[\s,()]/g, "")))
      ) {
        failed.push(
          `Year ${
            year.reportingYear
          }. The sum of all Income items less the sum of all Direct Expense items (${calculatedGrossProfit.toLocaleString()}) does not equal Gross Profit (${parseInt(
            year.grossProfit.replace(/,/g, "")
          ).toLocaleString()}) as read from the document.`
        );
      } else {
        succeeded.push(
          `Year ${
            year.reportingYear
          }. The sum of all Income items less the sum of all Direct Expense items (${calculatedGrossProfit.toLocaleString()}) equals Gross Profit (${parseInt(
            year.grossProfit.replace(/,/g, "")
          ).toLocaleString()}) as read from the document.`
        );
      }

      const totalOverheadExpense = parseInt(
        year.totalOverheadExpense.replace(/[\s,()]/g, "")
      );
      const operatingProfit = parseInt(
        year.operatingProfit.replace(/[\s,()]/g, "")
      );
      const grossProfit = parseInt(year.grossProfit.replace(/[\s,()]/g, ""));

      const adminExpense = parseInt(year.adminExpense.replace(/[\s,()]/g, ""));
      const otherNonOperatingExpense = parseInt(
        year.otherNonOperatingExpense.replace(/[\s,()]/g, "")
      );
      const salaryAndBonus = parseInt(
        year.salaryAndBonus.replace(/[\s,()]/g, "")
      );
      const depreciation = parseInt(year.depreciation.replace(/[\s,()]/g, ""));
      const intercompanyOverheadExpense = parseInt(
        year.intercompanyOverheadExpense.replace(/[\s,()]/g, "")
      );
      const otherIncome = parseInt(year.otherIncome.replace(/[\s,()]/g, ""));
      const netIncomeAfterTax = parseInt(
        year.netIncomeAfterTax.replace(/[\s,()]/g, "")
      );
      const discretionaryBonus = parseInt(
        year.discretionaryBonus.replace(/[\s,()]/g, "")
      );

      const sumA2 =
        Math.abs(grossProfit) -
        (Math.abs(adminExpense) +
          Math.abs(otherNonOperatingExpense) +
          Math.abs(salaryAndBonus) +
          Math.abs(depreciation) +
          Math.abs(intercompanyOverheadExpense));

      const sumA3 =
        Math.abs(totalOverheadExpense) ||
        Math.abs(adminExpense) +
          Math.abs(otherNonOperatingExpense) +
          Math.abs(salaryAndBonus) +
          Math.abs(depreciation) +
          Math.abs(intercompanyOverheadExpense);

      if (totalOverheadExpense != 0) {
        if (grossProfit - sumA3 !== operatingProfit) {
          failed.push(
            `Year ${
              year.reportingYear
            }. Gross Profit (less) Total Overhead Expense (${(
              grossProfit - totalOverheadExpense
            ).toLocaleString()}) does not equal Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`
          );
        } else {
          succeeded.push(
            `Year ${
              year.reportingYear
            }. Gross Profit (less) Total Overhead Expense (${(
              grossProfit - totalOverheadExpense
            ).toLocaleString()}) equals Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`
          );
        }
      } else {
        if (sumA2 !== operatingProfit) {
          failed.push(
            `Year ${
              year.reportingYear
            }. Gross Profit (less) all Indirect Expenses (${sumA2.toLocaleString()}) does not equal Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`
          );
        } else {
          succeeded.push(
            `Year ${
              year.reportingYear
            }. Gross Profit (less) all Indirect Expenses (${sumA2.toLocaleString()}) equals Operating Profit (${operatingProfit.toLocaleString()}) as read from the document.`
          );
        }
      }

      const interestExpense = parseInt(
        year.interestExpense.replace(/[\s,()]/g, "")
      );

      const incomeTaxCurrent = parseInt(
        year.incomeTaxCurrent.replace(/[\s,()]/g, "")
      );

      const incomeTaxDeferred = parseInt(
        year.incomeTaxDeferred.replace(/[\s,()]/g, "")
      );

      console.log("P & L");
      console.log("Operating Profit:", operatingProfit);
      console.log("Other Income:", otherIncome);
      console.log("Discretionary Bonus:", discretionaryBonus);
      console.log("Interest Expense:", interestExpense);
      console.log("Income Tax Current:", incomeTaxCurrent);
      console.log("Income Tax Deferred:", incomeTaxDeferred);

      const result =
        operatingProfit +
        otherIncome -
        (discretionaryBonus +
          interestExpense +
          incomeTaxCurrent +
          incomeTaxDeferred);

      console.log("Final Result:", result);

      if (operatingProfit !== 0) {
        if (netIncomeAfterTax !== result) {
          failed.push(
            `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) does not match the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`
          );
        } else {
          succeeded.push(
            `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${result.toLocaleString()}) matches the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`
          );
        }
      } else {
        if (
          netIncomeAfterTax !==
          sumA2 +
            otherIncome -
            (discretionaryBonus +
              interestExpense +
              incomeTaxCurrent +
              incomeTaxDeferred)
        ) {
          failed.push(
            `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${
              sumA2 +
              otherIncome -
              (
                discretionaryBonus +
                interestExpense +
                incomeTaxCurrent +
                incomeTaxDeferred
              ).toLocaleString()
            }) does not match the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`
          );
        } else {
          succeeded.push(
            `Year ${
              year.reportingYear
            }. Operating Profit (plus) Other Income (less) Non-Operating Expenses (${
              sumA2 +
              otherIncome -
              (
                discretionaryBonus +
                interestExpense +
                incomeTaxCurrent +
                incomeTaxDeferred
              ).toLocaleString()
            }) matches the value of Net Income After Tax (${year.netIncomeAfterTax.toLocaleString()}) as read from the document.`
          );
        }
      }
    }

    const workInProgressSplits = splitFilesData.filter(
      (data) => (data.splitFiles?.[0]?.labellerModel ?? "") === "workInProgress"
    );

    const workCompletedSplits = splitFilesData.filter(
      (data) => (data.splitFiles?.[0]?.labellerModel ?? "") === "workCompleted"
    );

    if (workInProgressSplits?.length > 0 || workCompletedSplits?.length > 0) {
      const maxYear = Math.max(
        ...getUniqueReportingYears(
          matchedSplit.splitFiles[0].labellerOutput.entities
        ).map((year) => parseInt(year))
      ).toString();

      console.log("Max Year: ", maxYear);

      const maxYearProperty =
        matchedSplit.splitFiles[0].labellerOutput.entities.find((entity) => {
          return entity.properties?.some(
            (property) =>
              property.type === "reporting-year" &&
              property.mentionText === maxYear
          );
        });

      const maxYearGrossContractIncome = maxYearProperty?.properties?.find(
        (prop) => prop.type === "gross-contract-income"
      )?.mentionText;

      console.log(
        `${maxYear} Gross Contract Income: `,
        maxYearGrossContractIncome
      );

      if (maxYearGrossContractIncome) {
        let currentYearEarnedRevenueSum = 0;

        const wipEntities = workInProgressSplits
          .flatMap((data) => {
            const asOfDate = data.splitFiles[0].labellerOutput.entities.find(
              (e) => e.type === "as_of_date" && e.mentionText.includes(maxYear)
            );
            if (asOfDate) {
              return data.splitFiles[0].labellerOutput.entities;
            }
            return null;
          })
          .filter((data) => data !== null);

        const workCompletedEntities = workCompletedSplits
          .flatMap((data) => {
            const asOfDate = data.splitFiles[0].labellerOutput.entities.find(
              (e) => e.type === "as_of_date" && e.mentionText.includes(maxYear)
            );
            if (asOfDate) {
              return data.splitFiles[0].labellerOutput.entities;
            }
            return null;
          })
          .filter((data) => data !== null);

        console.log("WIP Entities: ", wipEntities);
        console.log("Work Completed Entities: ", workCompletedEntities);

        const wipAndWorkCompletedEntities = [
          ...workCompletedEntities,
          ...wipEntities,
        ];

        if (wipAndWorkCompletedEntities.length > 0) {
          for (const entity of wipAndWorkCompletedEntities) {
            if (entity.type === "project") {
              const currentYearEarnedRevenue =
                entity.properties?.find(
                  (prop) => prop.type === "current_year_earned_revenue"
                )?.mentionText || "0";

              console.log(
                "current year earned revenue ",
                currentYearEarnedRevenue
              );

              if (currentYearEarnedRevenue) {
                currentYearEarnedRevenueSum += parseInt(
                  currentYearEarnedRevenue.replace(/[\s,()]/g, "")
                );
              }
            }
          }

          if (
            currentYearEarnedRevenueSum !=
            parseInt(maxYearGrossContractIncome.replace(/[\s,()]/g, ""))
          ) {
            failed.push(
              `The sum of current year earned revenue ${currentYearEarnedRevenueSum.toLocaleString()} as calculated from WIP and Work Completed documents does not equal the value of Gross Contract Income ${maxYearGrossContractIncome.toLocaleString()} as read from the Profit and Loss Statement`
            );
          } else {
            succeeded.push(
              `The sum of current year earned revenue ${currentYearEarnedRevenueSum.toLocaleString()} as calculated from WIP and Work Completed documents equals the value of Gross Contract Income ${maxYearGrossContractIncome.toLocaleString()} as read from the Profit and Loss Statement`
            );
          }
        }
      }
    }

    if (failed.length > 0) {
      return {
        succeeded: false,
        failedMessages: failed,
        succeededMessages: succeeded,
      };
    }

    return {
      succeeded: true,
      failedMessages: failed,
      succeededMessages: succeeded,
    };
  },
  cashflow: (splitFilesData) => {
    let failed = [],
      succeeded = [];

    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "cashflow"
    );

    if (!matchedSplit) {
      return { succeeded: true };
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "reporting-dates"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        failed.push(
          `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`
        );
      } else {
        succeeded.push(
          `Reporting date format validation passed: "${reportingDate}"`
        );
      }
    }

    let formula = getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).map((year) => ({
      reportingYear: year,
      netIncome: "",
      depreciationCf: "",
      badDebtExpense: "",
      otherNonCash: "",
      incomeTaxDeferredCf: "",
      changeInAssets: "",
      changeInLiabilities: "",
      netCashOperating: "",
      netCashInvesting: "",
      netCashFinancing: "",
      netCashFlow: "",
    }));

    getUniqueReportingYears(
      matchedSplit.splitFiles[0].labellerOutput.entities
    ).forEach((year, index) => {
      const matchedYearEntities =
        matchedSplit.splitFiles[0].labellerOutput.entities.filter((entity) => {
          return Boolean(
            entity.properties?.find(
              (prop) =>
                prop.type === "reporting-year" && prop.mentionText === year
            )
          );
        });

      const mergedProperties = matchedYearEntities.flatMap(
        (entity) => entity.properties ?? []
      );

      formula[index].netIncome = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-income"
      );
      formula[index].depreciationCf = sumPropertyValuesWithNegative(
        mergedProperties,
        "depreciation-cf"
      );
      formula[index].badDebtExpense = sumPropertyValuesWithNegative(
        mergedProperties,
        "bad-debt-expense"
      );
      formula[index].otherNonCash = sumPropertyValuesWithNegative(
        mergedProperties,
        "other-non-cash"
      );
      formula[index].incomeTaxDeferredCf = sumPropertyValuesWithNegative(
        mergedProperties,
        "income-tax-deferred-cf"
      );
      formula[index].changeInAssets = sumPropertyValuesWithNegative(
        mergedProperties,
        "change-in-assets"
      );
      formula[index].changeInLiabilities = sumPropertyValuesWithNegative(
        mergedProperties,
        "change-in-liabilities"
      );
      formula[index].netCashOperating = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-operating"
      );
      formula[index].netCashInvesting = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-investing"
      );
      formula[index].netCashFinancing = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-financing"
      );
      formula[index].netCashFlow = sumPropertyValuesWithNegative(
        mergedProperties,
        "net-cash-flow"
      );
    });

    for (const year of formula) {
      const netCashFlow = parseInt(year.netCashFlow.replace(/[\s,()]/g, ""));

      const netCashOperating = parseInt(
        year.netCashOperating.replace(/[\s,()]/g, "")
      );

      const netIncome = parseInt(year.netIncome);
      const depreciationCf = parseInt(year.depreciationCf);
      const badDebtExpense = parseInt(year.badDebtExpense);
      const otherNonCash = parseInt(year.otherNonCash);
      const incomeTaxDeferredCf = parseInt(year.incomeTaxDeferredCf);
      const changeInAssets = parseInt(year.changeInAssets);
      const changeInLiabilities = parseInt(year.changeInLiabilities);

      console.log("Net Income:", netIncome);
      console.log("Depreciation CF:", depreciationCf);
      console.log("Bad Debt Expense:", badDebtExpense);
      console.log("Other Non-Cash:", otherNonCash);
      console.log("Income Tax Deferred CF:", incomeTaxDeferredCf);
      console.log("Change in Assets:", changeInAssets);
      console.log("Change in Liabilities:", changeInLiabilities);

      const sumA =
        netIncome +
        depreciationCf +
        badDebtExpense +
        otherNonCash +
        incomeTaxDeferredCf +
        changeInAssets +
        changeInLiabilities;

      console.log("Total SumA:", sumA);

      if (netCashOperating !== sumA) {
        failed.push(
          `Year ${
            year.reportingYear
          }. Net Cash from Operating Activities (${netCashOperating.toLocaleString()}) does not match the calculated sum (${sumA.toLocaleString()}). Please review the components of Net Income, Depreciation, Bad Debt Expense, Other Non-Cash Items, Deferred Income Tax, and changes in Assets and Liabilities.`
        );
      } else {
        succeeded.push(
          `Year ${
            year.reportingYear
          }. Net Cash from Operating Activities (${netCashOperating.toLocaleString()}) matches the calculated sum (${sumA.toLocaleString()}). Please review the components of Net Income, Depreciation, Bad Debt Expense, Other Non-Cash Items, Deferred Income Tax, and changes in Assets and Liabilities.`
        );
      }

      const sumB =
        parseInt(year.netCashOperating) +
        parseInt(year.netCashInvesting) +
        parseInt(year.netCashFinancing);

      if (netCashFlow != sumB) {
        failed.push(
          `Year ${
            year.reportingYear
          }. Net Cash Flow (${netCashFlow.toLocaleString()}) The sum of Net Cash from Operating (${year.netCashOperating.toLocaleString()}), Investing (${year.netCashInvesting.toLocaleString()}), and Financing Activities (${year.netCashFinancing.toLocaleString()}) does not match Net Cash Flow (${year.netCashFlow.toLocaleString()}) as read from the document.`
        );
      } else {
        succeeded.push(
          `Year ${
            year.reportingYear
          }. Net Cash Flow (${netCashFlow.toLocaleString()}) The sum of Net Cash from Operating (${year.netCashOperating.toLocaleString()}), Investing (${year.netCashInvesting.toLocaleString()}), and Financing Activities (${year.netCashFinancing.toLocaleString()}) matches Net Cash Flow (${year.netCashFlow.toLocaleString()}) as read from the document.`
        );
      }
    }

    if (failed.length > 0) {
      return {
        succeeded: false,
        failedMessages: failed,
        succeededMessages: succeeded,
      };
    }

    return {
      succeeded: true,
      failedMessages: failed,
      succeededMessages: succeeded,
    };
  },
  workInProgress: (splitFilesData) => {
    let failed = [],
      succeeded = [];
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "workInProgress"
    );

    if (!matchedSplit) {
      return { succeeded: true };
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return { succeeded: true };
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "as_of_date"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        failed.push(
          `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`
        );
      } else {
        succeeded.push(
          `Reporting date format validation passed: "${reportingDate}"`
        );
      }
    }

    let formula = matchedSplit.splitFiles[0].labellerOutput.entities
      .filter((entity) => entity.type === "project")
      .map((entity) => {
        console.log("Wip entity.propertuies ", entity.properties);

        const projectName =
          entity.properties?.find((prop) => prop.type === "project_name")
            ?.mentionText || "Unknown Project";
        const estimatedGrossProfit = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "estimated-gross-profit")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        const estimatedCostOfRevenue = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "estimated_cost_of_revenue")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        const projectValueOrEstimatedRevenue = parseInt(
          entity.properties
            ?.find((prop) => prop.type === "project_value_or_estimated_revenue")
            ?.mentionText?.replace(/[\s,()]/g, "") ?? "0"
        );

        return {
          projectName,
          estimatedGrossProfit,
          estimatedCostOfRevenue,
          projectValueOrEstimatedRevenue,
        };
      });

    for (const project of formula) {
      if (
        !project.estimatedGrossProfit &&
        !project.projectValueOrEstimatedRevenue &&
        !project.estimatedCostOfRevenue
      ) {
        failed.push(
          `Project ${project.projectName}. Estimated Gross Profit, Estimated Revenue and Estimated Cost of Revenue are missing.`
        );
      } else {
        succeeded.push(
          `Project ${project.projectName}. Atleast 2 of the following are present: Estimated Gross Profit, Estimated Revenue and Estimated Cost of Revenue.`
        );
      }
    }

    if (failed.length > 0) {
      return {
        succeeded: false,
        failedMessages: failed,
        succeededMessages: succeeded,
      };
    }

    return {
      succeeded: true,
      failedMessages: failed,
      succeededMessages: succeeded,
    };
  },

  workCompleted: (splitFilesData) => {
    let failed = [],
      succeeded = [];
    const matchedSplit = splitFilesData.find(
      (data) => data.splitFiles[0].labellerModel === "workCompleted"
    );

    if (!matchedSplit) {
      return { succeeded: true };
    }

    if (!matchedSplit.splitFiles[0].labellerOutput.entities) {
      return { succeeded: true };
    }

    const reportingDate =
      matchedSplit.splitFiles[0].labellerOutput.entities.find(
        ({ type }) => type === "as_of_date"
      )?.mentionText ?? "";

    if (reportingDate) {
      const dateFormatRegex =
        /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/i;

      if (!dateFormatRegex.test(reportingDate)) {
        failed.push(
          `Invalid reporting date format. Expected format: "Month Day, Year" (e.g., "December 31, 2023"). Received: "${reportingDate}" (will prevent transfer data if not corrected)`
        );
      } else {
        succeeded.push(
          `Reporting date format validation passed: "${reportingDate}"`
        );
      }
    }

    if (failed.length > 0) {
      return {
        succeeded: false,
        failedMessages: failed,
        succeededMessages: succeeded,
      };
    }

    return {
      succeeded: true,
      failedMessages: failed,
      succeededMessages: succeeded,
    };
  },
};
